/**
 * DataBest — Application router (F1).
 * Wires:
 *   /login — public
 *   protected routes under <AppLayout>:
 *     /dashboard
 *     /admin/companies, /providers, /group-homes, /houses, /users
 */
import React, { useEffect } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    Outlet,
} from "react-router-dom";
import LoginPage from "@/features/auth/pages/LoginPage";
import AppLayout from "@/layouts/AppLayout";
import ProtectedRoute from "@/router/ProtectedRoute";
import DashboardPage from "@/features/dashboard/pages/DashboardPage";
import CompaniesPage from "@/features/organizations/pages/CompaniesPage";
import ProvidersPage from "@/features/organizations/pages/ProvidersPage";
import GroupHomesPage from "@/features/organizations/pages/GroupHomesPage";
import HousesPage from "@/features/organizations/pages/HousesPage";
import UsersAdminPage from "@/features/admin/pages/UsersAdminPage";
import DictionariesPage from "@/features/admin/pages/DictionariesPage";
import IndividualsListPage from "@/features/individuals/pages/IndividualsListPage";
import IndividualDetailPage from "@/features/individuals/pages/IndividualDetailPage";
import PlanBuilderPage from "@/features/plans/pages/PlanBuilderPage";
import TodayPage from "@/features/scoring/pages/TodayPage";
import BacklogPage from "@/features/backlog/pages/BacklogPage";
import HistoryPage from "@/features/history/pages/HistoryPage";
import BirpReportsPage from "@/features/birp/pages/BirpReportsPage";
import AssignmentsPage from "@/features/assignments/pages/AssignmentsPage";
import CaregiverPerfListPage from "@/features/caregiver-performance/pages/CaregiverPerfListPage";
import CaregiverPerfDetailPage from "@/features/caregiver-performance/pages/CaregiverPerfDetailPage";
import RoleLandingRedirect from "@/router/RoleLandingRedirect";
import useAuthStore from "@/store/authStore";
import { Toaster } from "sonner";

const Protected = () => (
    <ProtectedRoute>
        <AppLayout />
    </ProtectedRoute>
);

const AppRouter = () => {
    const hydrate = useAuthStore((s) => s.hydrate);

    useEffect(() => {
        hydrate();
    }, [hydrate]);

    return (
        <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
                <Route path="/" element={<RoleLandingRedirect />} />
                <Route path="/login" element={<LoginPage />} />

                <Route element={<Protected />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/today" element={<TodayPage />} />
                    <Route
                        path="/backlog"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                    "caregiver",
                                ]}
                            >
                                <BacklogPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/history"
                        element={
                            <ProtectedRoute
                                roles={["caregiver", "supervisor"]}
                            >
                                <HistoryPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                    "caregiver",
                                ]}
                            >
                                <BirpReportsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/individuals"
                        element={<IndividualsListPage />}
                    />
                    <Route
                        path="/individuals/:id"
                        element={<IndividualDetailPage />}
                    />
                    <Route
                        path="/assignments"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                    "analyst",
                                ]}
                            >
                                <AssignmentsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports/caregiver-performance"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                    "analyst",
                                    "caregiver",
                                ]}
                            >
                                <CaregiverPerfListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports/caregiver-performance/:caregiver_id"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                    "analyst",
                                    "caregiver",
                                ]}
                            >
                                <CaregiverPerfDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/plans/:id"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                ]}
                            >
                                <PlanBuilderPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/companies"
                        element={
                            <ProtectedRoute
                                roles={["system_admin", "company_admin"]}
                            >
                                <CompaniesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/providers"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "analyst",
                                ]}
                            >
                                <ProvidersPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/group-homes"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                    "analyst",
                                ]}
                            >
                                <GroupHomesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/houses"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                    "analyst",
                                ]}
                            >
                                <HousesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/users"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                    "supervisor",
                                ]}
                            >
                                <UsersAdminPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/dictionaries"
                        element={
                            <ProtectedRoute
                                roles={[
                                    "system_admin",
                                    "company_admin",
                                    "provider_admin",
                                ]}
                            >
                                <DictionariesPage />
                            </ProtectedRoute>
                        }
                    />
                </Route>

                <Route path="*" element={<RoleLandingRedirect />} />
            </Routes>
        </BrowserRouter>
    );
};

export default AppRouter;
